import React, { useEffect, useState } from "react";
import { ClickAwayListener, Popper } from "@mui/material";
import { linkColor, linkKeyAt, linkKeyIndex } from "./linkColors";

const UNLINKED_BAR = "var(--color-ui-border-strong)";

/**
 * The coloured bar that sits beside a row's "#n" and links an order item to
 * its packages. Clicking it opens the colour panel.
 *
 * Popper portals to document.body by default, which is what we want here:
 * both Ship page tables become `overflow: auto` scroll boxes under 1100px
 * (PackageModel.css) and their action columns are sticky with their own
 * stacking contexts, so an in-flow panel would be clipped or painted under
 * them.
 */
export default function LinkColorPicker({
  value,
  onChange,
  usage = {},
  slots = 12,
  title = "Link colour",
  showUsageList = false,
  height = 22,
  width = 4,
  disabled = false,
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const colour = linkColor(value);

  const close = () => setAnchorEl(null);

  const handleToggle = (event) => {
    if (disabled) return;
    const target = event.currentTarget;
    setAnchorEl((prev) => (prev ? null : target));
  };

  // The panel holds no focus of its own (the trigger keeps it), so a keydown
  // handler on the panel would never fire — listen on the document instead.
  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") setAnchorEl(null);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open]);

  const select = (key) => {
    onChange(key);
    close();
  };

  // Always offer at least one free colour past the ones already taken, so a
  // shipment with more products than the default slot count never runs out.
  const slotCount = Math.max(slots, linkKeyIndex(value) + 1);
  const usedKeys = Object.keys(usage || {});
  const label = value ? (usage || {})[value] : "";

  return (
    <>
      <button
        // Ship.jsx renders both tables inside a <Form>; without this every
        // click on a bar would submit the shipment.
        type="button"
        onClick={handleToggle}
        disabled={disabled}
        aria-label={label ? `${title}: ${label}` : `${title}: none`}
        title={label || title}
        style={{
          // Explicit px: `.package__list_item *` forces font-size to 0.7vw
          // with !important, so anything em-sized would collapse in this cell.
          width,
          height,
          minWidth: width,
          marginLeft: 4,
          padding: 0,
          border: 0,
          borderRadius: 999,
          background: colour || UNLINKED_BAR,
          opacity: colour ? 1 : 0.45,
          cursor: disabled ? "default" : "pointer",
          flexShrink: 0,
        }}
      />

      {open && (
        <Popper
          open={open}
          anchorEl={anchorEl}
          placement="bottom-start"
          // Above MUI's modal layer (1300) so the panel still works from
          // inside the bundle and saved-package modals.
          style={{ zIndex: 1500 }}
          modifiers={[
            { name: "offset", options: { offset: [0, 6] } },
            { name: "preventOverflow", options: { padding: 8 } },
          ]}
        >
          <ClickAwayListener onClickAway={close}>
            <div className="w-[228px] rounded-lg border border-ui-border bg-ui-surface-raised p-2 shadow-ui-md">
              <div className="mb-1.5 text-[11px] font-semibold text-ui-text">
                {title}
              </div>

              {showUsageList && usedKeys.length > 0 && (
                <div className="mb-2 max-h-[150px] overflow-y-auto border-b border-ui-border-subtle pb-2">
                  {usedKeys.map((key) => (
                    <button
                      type="button"
                      key={key}
                      onClick={() => select(key)}
                      className="flex w-full items-center gap-2 rounded px-1 py-1 text-left hover:bg-ui-row-hover"
                    >
                      <span
                        style={{
                          width: 4,
                          height: 16,
                          borderRadius: 999,
                          background: linkColor(key),
                          flexShrink: 0,
                        }}
                      />
                      <span className="truncate text-[11px] text-ui-text">
                        {(usage || {})[key]}
                      </span>
                      {value === key && (
                        <span className="ml-auto text-[11px] text-brand">
                          ✓
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              )}

              <div className="grid grid-cols-6 gap-1">
                {Array.from({ length: slotCount }, (_, i) => {
                  const key = linkKeyAt(i + 1);
                  const owner = (usage || {})[key];
                  return (
                    <button
                      type="button"
                      key={key}
                      onClick={() => select(key)}
                      title={owner ? `${key} — ${owner}` : key}
                      className="flex items-center justify-center rounded py-1 hover:bg-ui-row-hover"
                      style={{
                        boxShadow:
                          value === key
                            ? "inset 0 0 0 1px var(--color-brand)"
                            : "none",
                      }}
                    >
                      <span
                        style={{
                          width: 4,
                          height: 20,
                          borderRadius: 999,
                          background: linkColor(key),
                        }}
                      />
                    </button>
                  );
                })}
              </div>

              <button
                type="button"
                onClick={() => select("")}
                className="mt-2 w-full rounded border border-ui-border px-2 py-1 text-[11px] text-ui-text-muted hover:bg-ui-row-hover"
              >
                Clear link
              </button>
            </div>
          </ClickAwayListener>
        </Popper>
      )}
    </>
  );
}
